import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchStats, fetchUpcoming } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { DestinationBadge } from "../components/DestinationBadge";
import { Users, Plane, Home, Clock, AlertCircle, CheckCircle2, MapPin, TrendingUp, Factory, Building2 } from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { cn } from "../lib/utils";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchStats(), fetchUpcoming(8)])
      .then(([s, u]) => { setStats(s); setUpcoming(u); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-zinc-400 text-center" data-testid="dashboard-loading">Loading dashboard...</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-8 lg:py-10" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="label-eyebrow mb-2">Operations Dashboard</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">Good day, Coordinator</h1>
            <p className="text-sm text-zinc-400 mt-1">Live view of travelers across Monterrey and Apodaca</p>
          </div>
          <div className="text-xs text-zinc-500 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
            Updated {format(new Date(), "MMM d · HH:mm")}
          </div>
        </div>

        {/* Primary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
          <KpiCard icon={Users} label="Active Travelers" value={stats.total_travelers} accent="orange" testid="kpi-total" />
          <KpiCard icon={MapPin} label="Currently In Town" value={stats.currently_in_town} accent="cyan" testid="kpi-in-town" />
          <KpiCard icon={Clock} label="Pending Confirmations" value={stats.pending_confirmations} accent="amber" testid="kpi-pending" />
          <KpiCard icon={CheckCircle2} label="Confirmed" value={stats.confirmed_total} accent="emerald" testid="kpi-confirmed" />
        </div>

        {/* Destination split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DestinationCard
            destination="Monterrey"
            icon={Factory}
            accent="#10B981"
            total={stats.monterey_total}
            week={stats.monterey_arrivals_week}
            heroUrl="https://images.unsplash.com/photo-1585975754487-53b23cff1a9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
          />
          <DestinationCard
            destination="Apodaca"
            icon={Building2}
            accent="#F59E0B"
            total={stats.apodaca_total}
            week={stats.apodaca_arrivals_week}
            heroUrl="https://images.unsplash.com/photo-1565041080441-bf4ee2a65b67?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
          />
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Plane} label="Arrivals This Week" value={stats.arrivals_this_week} size="sm" testid="kpi-arrivals-week" />
          <KpiCard icon={TrendingUp} label="Departures This Week" value={stats.departures_this_week} size="sm" testid="kpi-departures-week" />
          <KpiCard icon={AlertCircle} label="Missing Lodging" value={stats.missing_lodging} size="sm" accent="orange" testid="kpi-missing-lodging" />
          <KpiCard icon={Home} label="Closed Records" value={stats.total_travelers - stats.confirmed_total - stats.pending_confirmations} size="sm" testid="kpi-closed" />
        </div>

        {/* Upcoming arrivals timeline */}
        <div className="glass-card p-6" data-testid="upcoming-arrivals">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-[#F07D00]" />
              <h2 className="font-heading text-lg font-medium text-white">Upcoming arrivals</h2>
            </div>
            <Link to="/admin/records" className="text-xs text-zinc-400 hover:text-[#F07D00] transition-colors" data-testid="view-all-records-link">
              View all records →
            </Link>
          </div>

          {upcoming.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">No upcoming arrivals</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map((t) => {
                const daysAway = differenceInDays(parseISO(t.arrival_date), new Date());
                return (
                  <Link
                    key={t.id}
                    to={`/admin/records/${t.id}`}
                    data-testid={`upcoming-${t.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg border border-transparent hover:border-white/10 hover:bg-white/5 transition-all group"
                  >
                    <div className="flex-shrink-0 w-14 text-center">
                      <div className="font-heading text-xl font-semibold text-white">{format(parseISO(t.arrival_date), "d")}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{format(parseISO(t.arrival_date), "MMM")}</div>
                    </div>
                    <div className="h-10 w-px bg-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm font-semibold text-white truncate">{t.full_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{t.purpose || "Business trip"} · {t.flight_number || "Flight TBD"}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <DestinationBadge destination={t.destination} size="sm" />
                    </div>
                    <div className="hidden md:block">
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#F07D00] w-20 text-right">
                      {daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : `In ${daysAway}d`}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ACCENTS = {
  orange: { ring: "ring-[#F07D00]/30", glow: "from-[#F07D00]/20", icon: "text-[#F07D00]" },
  cyan: { ring: "ring-cyan-500/30", glow: "from-cyan-500/20", icon: "text-cyan-400" },
  emerald: { ring: "ring-emerald-500/30", glow: "from-emerald-500/20", icon: "text-emerald-400" },
  amber: { ring: "ring-amber-500/30", glow: "from-amber-500/20", icon: "text-amber-400" },
};

function KpiCard({ icon: Icon, label, value, accent = "default", size = "md", testid }) {
  const a = ACCENTS[accent];
  return (
    <div className={cn("glass-card glass-card-hover p-4 sm:p-5 relative overflow-hidden", a && `ring-1 ${a.ring}`)} data-testid={testid}>
      {a && <div className={cn("absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl bg-gradient-to-br", a.glow, "to-transparent")} />}
      <div className="relative flex items-start justify-between mb-3">
        <Icon className={cn("h-5 w-5", a ? a.icon : "text-zinc-500")} />
      </div>
      <div className={cn("font-heading font-semibold text-white tracking-tight", size === "sm" ? "text-2xl" : "text-3xl sm:text-4xl")}>
        {value}
      </div>
      <div className="label-eyebrow mt-1.5">{label}</div>
    </div>
  );
}

function DestinationCard({ destination, label, icon: Icon, accent, total, week, heroUrl }) {
  return (
    <div className="glass-card glass-card-hover relative overflow-hidden p-6 min-h-[180px]" data-testid={`destination-card-${destination.toLowerCase()}`}>
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${heroUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 destination-card-overlay" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <Icon className="h-5 w-5" style={{ color: accent }} />
          <span className="label-eyebrow">{label || destination}</span>
        </div>
        <div className="flex items-end gap-6">
          <div>
            <div className="font-heading text-5xl font-semibold text-white tracking-tight">{total}</div>
            <div className="text-xs text-zinc-400 mt-1">Total travelers</div>
          </div>
          <div className="h-12 w-px bg-white/10" />
          <div>
            <div className="font-heading text-2xl font-semibold text-white">{week}</div>
            <div className="text-xs text-zinc-400 mt-1">This week</div>
          </div>
        </div>
      </div>
    </div>
  );
}

